import React, { forwardRef, useCallback, useEffect, useRef, useState, type ComponentPropsWithRef } from "react";

import { cva } from "class-variance-authority";

import { PlanningType } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { cn } from "@eisox/design-system";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import { createContext } from "@radix-ui/react-context";

import { HybridTooltip } from "~/components/hybrid-tooltip";
import { Tooltip } from "~/components/tooltip";
import { m } from "~/paraglide/messages";

import { planningTypeKeyMessages } from "../helpers";

/** --------------------------------------------------------------------------------------------------------------------
 * Week
 * ------------------------------------------------------------------------------------------------------------------ */

type WeekProps = ComponentPropsWithRef<"div">;

const Root = forwardRef<HTMLDivElement, WeekProps>(({ className, ...props }, forwardedRef) => (
  <div ref={forwardedRef} className={cn("flex gap-[10px]", className)} {...props} />
));

Root.displayName = "Week.Root";

/** --------------------------------------------------------------------------------------------------------------------
 * Day
 * ------------------------------------------------------------------------------------------------------------------ */

const DAY_NAME = "Week.Day";

interface WeekDayContextValue {
  timeslots: TimeslotElement[];
  timeslotRefCallback: (node: TimeslotElement | null) => void;
}

const [WeekDayProvider, useWeekDayProvider] = createContext<WeekDayContextValue>(DAY_NAME);

interface DayProps extends ComponentPropsWithRef<"div"> {
  day: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  hourTooltipPosition?: "left" | "top" | "right" | "bottom";
}

const Day = forwardRef<HTMLDivElement, DayProps>((props, forwardedRef) => {
  const { day, hourTooltipPosition = "left", className, children, ...dayProps } = props;

  const [currentHour, setCurrentHour] = useState(dayjs().format("HH:mm"));

  const timeslots = useRef<TimeslotElement[]>([]).current;

  const currentDay = dayjs().day() === day;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHour(dayjs().format("HH:mm"));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const indicatorPadding = 10;
  const indicatorTop = `calc(${calculateTop(currentHour)}% - ${indicatorPadding}px)`;

  const timeslotRefCallback = useCallback(
    (node: TimeslotElement | null) => {
      if (node) timeslots.push(node);
    },
    [timeslots],
  );

  return (
    <WeekDayProvider timeslots={timeslots} timeslotRefCallback={timeslotRefCallback}>
      <div ref={forwardedRef} className={cn("flex flex-1 flex-col items-center gap-[10px]", className)} {...dayProps}>
        <div className={cn("text-base uppercase", currentDay && "font-bold underline")}>
          {dayjs().day(day).format("ddd").slice(0, 1)}
        </div>
        <div className="relative aspect-1/5 w-full flex-1 overflow-hidden rounded-[10px] bg-primary-gray">
          {children}
          {currentDay && (
            <Tooltip className="p-[15px]" content={currentHour} position={hourTooltipPosition} keepHoverOpen={false}>
              <div
                className="absolute flex w-full items-center justify-center after:absolute after:top-1/2 after:h-[2px] after:w-[80%] after:rounded-[2px] after:bg-primary-white after:content-['']"
                style={{ top: indicatorTop, padding: indicatorPadding }}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </WeekDayProvider>
  );
});

Day.displayName = "Day";

/** --------------------------------------------------------------------------------------------------------------------
 * Timeslot
 * ------------------------------------------------------------------------------------------------------------------ */

const timeslotVariants = cva("absolute w-full", {
  variants: {
    type: {
      comfort: "bg-planning-comfort",
      night: "bg-planning-night",
      absence: "bg-planning-absence",
      preComfort: "bg-planning-pre-comfort",
      occupation: "bg-planning-comfort",
      inoccupation: "bg-planning-absence",
      thermalShock: "bg-planning-comfort",
      enabled: "bg-planning-comfort",
      stop: "bg-planning-stop",
    },
    userDefined: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      type: "comfort",
      userDefined: true,
      className:
        "[background:repeating-linear-gradient(135deg,#e4a46a,#e4a46a_1px,var(--planning-comfort-color)_1px,var(--planning-comfort-color)_5px)]",
    },
    {
      type: "night",
      userDefined: true,
      className:
        "[background:repeating-linear-gradient(135deg,#c8c6eb,#c8c6eb_1px,var(--planning-night-color)_1px,var(--planning-night-color)_5px)]",
    },
    {
      type: "absence",
      userDefined: true,
      className:
        "[background:repeating-linear-gradient(135deg,#c8c6eb,#c8c6eb_1px,var(--planning-absence-color)_1px,var(--planning-absence-color)_5px)]",
    },
    {
      type: "occupation",
      userDefined: true,
      className:
        "[background:repeating-linear-gradient(135deg,#e4a46a,#e4a46a_1px,var(--planning-comfort-color)_1px,var(--planning-comfort-color)_5px)]",
    },
    {
      type: "inoccupation",
      userDefined: true,
      className:
        "[background:repeating-linear-gradient(135deg,#c8c6eb,#c8c6eb_1px,var(--planning-absence-color)_1px,var(--planning-absence-color)_5px)]",
    },
    {
      type: "thermalShock",
      userDefined: true,
      className:
        "[background:repeating-linear-gradient(135deg,#e4a46a,#e4a46a_1px,var(--planning-comfort-color)_1px,var(--planning-comfort-color)_5px)]",
    },
    {
      type: "enabled",
      userDefined: true,
      className:
        "[background:repeating-linear-gradient(135deg,#e4a46a,#e4a46a_1px,var(--planning-comfort-color)_1px,var(--planning-comfort-color)_5px)]",
    },
    {
      type: "stop",
      userDefined: true,
      className:
        "[background:repeating-linear-gradient(135deg,#cb8385,#cb8385_1px,var(--planning-stop-color)_1px,var(--planning-stop-color)_5px)]",
    },
  ],
});

type TimeslotElement = React.ElementRef<typeof Timeslot>;

const TIMESLOT_NAME = "Week.Timeslot";
interface TimeslotProps extends ComponentPropsWithRef<"div"> {
  start: string;
  end: string;
  type: PlanningType;
  userDefined?: boolean;
  temperature?: number;
  tooltipPosition?: "left" | "top" | "right" | "bottom";
}

const Timeslot = forwardRef<HTMLDivElement, TimeslotProps>((props, forwardedRef) => {
  const {
    start,
    end,
    type,
    userDefined = false,
    tooltipPosition = "left",
    temperature,
    className,
    style,
    ...timeslotProps
  } = props;

  const { timeslots, timeslotRefCallback } = useWeekDayProvider(TIMESLOT_NAME);

  const timeslotRef = useRef<HTMLDivElement>(null);
  const composedRefs = useComposedRefs(timeslotRef, node => timeslotRefCallback(node), forwardedRef);

  const [displayHourStart, setDisplayHourStart] = useState(false);
  const [displayHourEnd, setDisplayHourEnd] = useState(false);

  const top = calculateTop(start);
  const bottom = calculateBottom(end);

  useEffect(() => {
    const LINE_HEIGHT = 21; // line-height
    const node = timeslotRef.current;
    if (!node) return;

    const resize = () => {
      const { top, height } = node.getBoundingClientRect();
      const otherTimeslots = timeslots.filter(t => t !== node);
      const displayHourStart = !otherTimeslots.some(t => {
        const rect = t.getBoundingClientRect();
        return rect.bottom >= top - LINE_HEIGHT && rect.bottom >= top;
      });
      setDisplayHourStart(displayHourStart && start !== "00:00");
      setDisplayHourEnd(height >= LINE_HEIGHT && end !== "00:00");
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(node);

    return () => observer.disconnect();
  }, [start, end, timeslots]);

  return (
    <HybridTooltip
      className="p-[15px]"
      content={
        <TimeslotTooltip start={start} end={end} type={type} userDefined={userDefined} temperature={temperature} />
      }
      position={tooltipPosition}
      keepHoverOpen={false}
    >
      <div
        ref={composedRefs}
        className={timeslotVariants({ type, userDefined, className })}
        style={{ top: `${top}%`, bottom: `${bottom}%`, ...style }}
        {...timeslotProps}
      >
        {displayHourStart && (
          <p className="top-0 pointer-events-none absolute w-full -translate-y-full cursor-default text-center text-[clamp(8px,2.5vw,var(--font-size-base))]">
            {start.replace(":", "h")}
          </p>
        )}
        {displayHourEnd && (
          <p className="bottom-0 pointer-events-none absolute w-full cursor-default text-center text-[clamp(8px,2.5vw,var(--font-size-base))]">
            {end.replace(":", "h")}
          </p>
        )}
      </div>
    </HybridTooltip>
  );
});

Timeslot.displayName = TIMESLOT_NAME;

const timeslotTooltipTypeVariants = cva(
  "before:mr-[10px] before:inline-block before:h-[16px] before:w-[16px] before:rounded-[2px] before:align-middle before:content-[''] [&_span]:text-primary-dark-gray",
  {
    variants: {
      type: {
        comfort: "before:bg-planning-comfort",
        night: "before:bg-planning-night",
        absence: "before:bg-planning-absence",
        preComfort: "before:bg-planning-pre-comfort",
        occupation: "before:bg-planning-comfort",
        inoccupation: "before:bg-planning-absence",
        thermalShock: "before:bg-planning-comfort",
        enabled: "before:bg-planning-comfort",
        stop: "before:bg-planning-stop",
      },
      userDefined: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        type: "comfort",
        userDefined: true,
        className:
          "before:[background:repeating-linear-gradient(135deg,#e4a46a,#e4a46a_1px,var(--planning-comfort-color)_1px,var(--planning-comfort-color)_5px)]",
      },
      {
        type: "night",
        userDefined: true,
        className:
          "before:[background:repeating-linear-gradient(135deg,#c8c6eb,#c8c6eb_1px,var(--planning-night-color)_1px,var(--planning-night-color)_5px)]",
      },
      {
        type: "absence",
        userDefined: true,
        className:
          "before:[background:repeating-linear-gradient(135deg,#c8c6eb,#c8c6eb_1px,var(--planning-absence-color)_1px,var(--planning-absence-color)_5px)]",
      },
      {
        type: "occupation",
        userDefined: true,
        className:
          "before:[background:repeating-linear-gradient(135deg,#e4a46a,#e4a46a_1px,var(--planning-comfort-color)_1px,var(--planning-comfort-color)_5px)]",
      },
      {
        type: "inoccupation",
        userDefined: true,
        className:
          "before:[background:repeating-linear-gradient(135deg,#c8c6eb,#c8c6eb_1px,var(--planning-absence-color)_1px,var(--planning-absence-color)_5px)]",
      },
      {
        type: "thermalShock",
        userDefined: true,
        className:
          "before:[background:repeating-linear-gradient(135deg,#e4a46a,#e4a46a_1px,var(--planning-comfort-color)_1px,var(--planning-comfort-color)_5px)]",
      },
      {
        type: "enabled",
        userDefined: true,
        className:
          "before:[background:repeating-linear-gradient(135deg,#e4a46a,#e4a46a_1px,var(--planning-comfort-color)_1px,var(--planning-comfort-color)_5px)]",
      },
      {
        type: "stop",
        userDefined: true,
        className:
          "before:[background:repeating-linear-gradient(135deg,#cb8385,#cb8385_1px,var(--planning-stop-color)_1px,var(--planning-stop-color)_5px)]",
      },
    ],
  },
);

interface TimeslotTooltipProps {
  start: string;
  end: string;
  type: PlanningType;
  userDefined?: boolean;
  temperature?: number;
}

const TimeslotTooltip: React.FC<TimeslotTooltipProps> = ({ start, end, type, userDefined = false, temperature }) => {
  const allDay = start === "00:00" && end === "00:00";

  return (
    <div className="flex flex-col gap-[10px] text-base">
      <div className={timeslotTooltipTypeVariants({ type, userDefined })}>
        {planningTypeKeyMessages[type]} {temperature && <span>({temperature}°C)</span>}
      </div>
      <p>{allDay ? m.all_day() : `${start.replace(":", "h")} - ${end.replace(":", "h")}`}</p>
    </div>
  );
};

TimeslotTooltip.displayName = "Week.TimeslotTooltip";

const calculateTop = (start: string) => {
  const [hours = 0, minutes = 0] = start.split(":").map(Number);
  return Math.round(((hours * 60 + minutes) / (24 * 60)) * 100);
};

const calculateBottom = (end: string) => {
  if (end === "00:00") return 0;
  return 100 - calculateTop(end);
};

export { Day, Root, Timeslot };
export type { DayProps, TimeslotProps, WeekProps };
